/**
 * 화면 하나를 지키는 겹 — 접근 거절 안내(EA-39).
 *
 * ⚠ **갈래를 나눠 보여 준다**(레거시 원칙 그대로). 하나로 뭉치면 PIN 만 안 푼
 * 사람이 「권한이 없습니다」를 읽고 관리자에게 항의하러 간다.
 *
 * 순서(명세 1절 EA-39): 불러오는 중 → 로그인 필요 → 처음 설정 → 계정 비활성 →
 * PIN 필요 → 권한 없음. NAV 에 없는 경로는 막지 않는다(레거시 그대로).
 */
import type { ReactNode } from 'react'
import { useSession } from '@/auth/session'
import { mayEnter } from '@/auth/nav'
import { Notice } from './ui'
import { ADMIN_STATUS_LABEL } from '@/api/types'

export function Guarded({ path, children }: { path: string; children: ReactNode }) {
  const { me, loading, setupNeeded, inactive, pinNeeded } = useSession()

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>
  if (!me) return <Notice tone="stop">Please sign in.</Notice>

  // ① 임시 비밀번호로 들어온 세션 — 비밀번호·PIN 을 정해야 일한다.
  if (setupNeeded) {
    return <Notice tone="warn">Finish the first setup to see this screen.</Notice>
  }

  // ② 계정 자체가 꺼져 있다 — 권한 문제가 아니다.
  if (inactive) {
    return (
      <Notice tone="stop">
        <strong>This admin account is not active.</strong>
        <br />
        Current status: {ADMIN_STATUS_LABEL[me.status]} · Please contact an administrator.
      </Notice>
    )
  }

  // ③ 키패드를 아직 안 지났다 — 로그아웃하지 않는다.
  if (pinNeeded) {
    return <Notice tone="warn">Enter your PIN to see this screen.</Notice>
  }

  // ④ 키가 없다.
  if (!mayEnter(path, { superAdmin: me.superAdmin, keys: me.keys })) {
    return <Notice tone="stop">You do not have permission to access this feature.</Notice>
  }

  return <>{children}</>
}
